import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, ReplaySubject, forkJoin, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class SourceLoaderService {
  private loadedScripts = new Map<string, ReplaySubject<void> | null>();
  private loadedStyles: Map<string, HTMLLinkElement | HTMLStyleElement> =
    new Map();

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  loadScript(
    src: string[] | string,
    place: 'body' | 'head' = 'body',
    defer: boolean = false,
    onload: () => void = () => {},
  ): Observable<void | void[]> {
    if (!isPlatformBrowser(this.platformId)) {
      return of();
    }

    if (Array.isArray(src)) {
      const loads = src.map((s) =>
        this.loadSingleScript(s, place, defer, onload),
      );
      return forkJoin(loads).pipe();
    } else {
      return this.loadSingleScript(src, place, defer, onload);
    }
  }

  private loadSingleScript(
    url: string,
    place: 'body' | 'head' = 'body',
    defer: boolean = false,
    onload: () => void = () => {},
  ): Observable<void> {
    if (this.loadedScripts.has(url)) {
      return this.loadedScripts.get(url)!.asObservable();
    }

    const subject = new ReplaySubject<void>(1);
    this.loadedScripts.set(url, subject);

    const existingScript = document.querySelector(`script[src="${url}"]`);
    if (existingScript) {
      subject.next();
      subject.complete();
      return subject.asObservable();
    }

    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.defer = defer;
    script.onload = () => {
      subject.next();
      subject.complete();

      onload();
    };
    script.onerror = () => {
      subject.error(new Error(`Error while loading script: ${url}`));
    };

    const node = place === 'body' ? document.body : document.head;

    node.appendChild(script);
    return subject.asObservable();
  }

  public deleteLoadedScript(url: string) {
    if (this.loadedScripts.has(url)) {
      this.loadedScripts.delete(url);
    }
  }

  loadStyle(href: string): void {
    if (this.loadedStyles.has(href) || !isPlatformBrowser(this.platformId)) {
      return;
    }

    const linkEl = document.createElement('link');
    linkEl.rel = 'stylesheet';
    linkEl.href = href;
    document.head.appendChild(linkEl);

    this.loadedStyles.set(href, linkEl);
  }

  removeStyle(href: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const linkEl = this.loadedStyles.get(href);

    if (linkEl) {
      document.head.removeChild(linkEl);
      this.loadedStyles.delete(href);
    }
  }

  loadInlineStyle(id: string, css: string): void {
    if (!isPlatformBrowser(this.platformId) || this.loadedStyles.has(id)) {
      return;
    }

    const styleEl = document.createElement('style');
    styleEl.id = id;
    styleEl.textContent = css;
    document.head.appendChild(styleEl);

    this.loadedStyles.set(id, styleEl);
  }

  removeInlineStyle(id: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const el = this.loadedStyles.get(id);

    if (el) {
      document.head.removeChild(el);
      this.loadedStyles.delete(id);
    }
  }
}
